'use client';

import { memo } from 'react';
import { ReactComponent as CalendarDay } from '@assets/icons/calendar-day.svg';
import { inputTimeFormat, outputTimeFormat, ROUTES } from 'constant';
import NextLink from 'next/link';
import { formatHandler, getCalendarDate } from 'services/datesAndTimes';
import { useGetClAppointmentsQuery } from 'services/userProfileApi';

import { Flex, Stack, useBreakpointValue, useTheme } from '@chakra-ui/react';

import { useIsAuthenticated } from 'store/hooks';

import { AppointmentsType } from 'types';

import { Text } from 'ui';

interface NextAppointmentProps {
  isMobile: boolean;
}

const NextAppointment = ({ isMobile }: NextAppointmentProps) => {
  const {
    colors: {
      palette: {
        components: { profileSidebar }
      }
    }
  } = useTheme();
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { fallback: 'base' }
  );
  const isAuthenticated = useIsAuthenticated();
  const { data: upcomingAppointments = [] } = useGetClAppointmentsQuery(
    {
      itemType: AppointmentsType.future
    },
    { skip: !isAuthenticated, refetchOnFocus: false, refetchOnReconnect: false }
  );

  const isUpcomingAppointments = upcomingAppointments?.length > 0;
  const nearestAppointment = isUpcomingAppointments
    ? upcomingAppointments[0]
    : null;
  const isFutureApptExist = isUpcomingAppointments && nearestAppointment;

  return isFutureApptExist ? (
    <Stack
      w='full'
      gap={0}
      my={isMobile ? 0 : '15px'}
      bg={isMobile ? profileSidebar.mobileBg : profileSidebar.desktopBg}
      overflow='hidden'
      alignItems='center'
      borderRadius={isMobile ? 0 : '3px'}
      pt={isMobile && isFutureApptExist ? '10px' : 0}
      pb={isMobile && isFutureApptExist ? '15px' : 0}
    >
      <Flex
        display={isMobile ? 'none' : 'flex'}
        py='4px'
        justifyContent='center'
        alignItems='center'
        bg={profileSidebar.apptLabelBg}
        paddingX={{ base: isMobile ? 0 : '14px', lg: '17px' }}
        w='100%'
      >
        <Text
          fontSize='12px'
          textTransform='uppercase'
          color={
            isMobile
              ? profileSidebar.calendarTextMobileColor
              : profileSidebar.calendarTextColor
          }
        >
          Next Appointment
        </Text>
      </Flex>

      <Flex
        as={NextLink}
        href={`${ROUTES.upcomingAppointment}/${nearestAppointment?.confirmation_num}`}
        alignItems='center'
        justifyContent='center'
        pl={{ base: isMobile ? 0 : '14px', lg: '17px' }}
        pr={{ base: isMobile ? 0 : '14px', lg: '12px' }}
        py={isMobile ? 0 : '20px'}
        gap='5px'
        w='100%'
      >
        <Stack
          flexDirection={{ base: 'column', sm: 'row' }}
          justifyContent='center'
          alignItems='center'
          px={{ base: '50px', sm: '0' }}
          gap={{ base: '6px', sm: '30px' }}
          flex={1}
        >
          <CalendarDay
            style={{ display: isDesktop ? 'block' : 'none' }}
            fill={profileSidebar.calendarIconFill}
            width='20px'
            height='23px'
          />
          <Text
            display={isMobile ? 'block' : 'none'}
            fontSize='12px'
            textTransform='uppercase'
            textAlign='center'
            lineHeight='10px'
            pt={{ base: '4px', lg: 0 }}
            color={
              isMobile
                ? profileSidebar.calendarTextMobileColor
                : profileSidebar.calendarTextColor
            }
          >
            Next Appointment
          </Text>

          <Text
            as='span'
            fontSize={isMobile ? '20px' : '16px'}
            fontWeight='semibold'
            textAlign='center'
            color={
              isMobile
                ? profileSidebar.calendarTextMobileColor
                : profileSidebar.calendarDateTextColor
            }
          >
            {getCalendarDate(upcomingAppointments?.[0]?.appt_start_dt)} at{' '}
            {formatHandler(
              upcomingAppointments?.[0]?.appt_start_tm,
              inputTimeFormat,
              outputTimeFormat
            )}
          </Text>
        </Stack>
      </Flex>
    </Stack>
  ) : null;
};

export default memo(NextAppointment);
